import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CreateInquiryStep2_5 } from '@webapp/core/interfaces';
import {
  COUNTRY_OPTIONS,
  FLOOR_OPTIONS,
  OBJECT_TYPE_OPTIONS,
} from '@webapp/core/constants/inquiry-labels';
import { ToastrService } from 'ngx-toastr';
import { GooglePlacesService } from 'projects/webapp/app/services/google-places.service';
import {
  InquiryDraftSource,
  PublicInquiryManagerService,
} from 'projects/webapp/app/services/public-inquiry-manager.service';


/** Einzug: where the move ends. */
@Component({
  selector: 'app-inquiry-step2-5',
  templateUrl: './step2.5.component.html',
})
export class InquiryStep25Component implements OnInit, OnDestroy {
  draftInquiryForm = new FormGroup({
    address: new FormControl('', { validators: Validators.required, nonNullable: true }),
    zip: new FormControl('', { validators: Validators.required, nonNullable: true }),
    city: new FormControl('', { validators: Validators.required, nonNullable: true }),
    country: new FormControl('CH', { validators: Validators.required, nonNullable: true }),
    object_type: new FormControl('', { validators: Validators.required, nonNullable: true }),
    floor: new FormControl('', { validators: Validators.required, nonNullable: true }),
    has_lift: new FormControl<boolean | null>(null, { validators: Validators.required }),
  });

  objectTypeOptions = OBJECT_TYPE_OPTIONS;

  floorOptions = FLOOR_OPTIONS;

  countryOptions = COUNTRY_OPTIONS;

  protected _draftSource: InquiryDraftSource = () => ({ step2_5: this._buildPayload() });

  constructor(
    protected _toastrService: ToastrService,
    protected _publicInquiryManagerService: PublicInquiryManagerService,
    private _googlePlacesService: GooglePlacesService,
  ) {
  }

  ngOnInit() {
    this._loadFormData();
    this._publicInquiryManagerService.setDraftSource(this._draftSource);
  }

  ngOnDestroy() {
    this._publicInquiryManagerService.clearDraftSource(this._draftSource);
  }

  onSubmit() {
    this.draftInquiryForm.markAllAsTouched();
    if (!this.draftInquiryForm.valid) {
      this._toastrService.warning('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    this._publicInquiryManagerService.submitStep2_5(this._buildPayload());
  }

  hasError(controlName: string, errorName?: string) {
    const control = this.draftInquiryForm.get(controlName);
    const isValid = errorName ? !control?.hasError(errorName) : !control?.invalid;
    return !!control?.touched && !isValid;
  }

  isHasLiftOptionSelected(value: boolean | null = null) {
    return this.draftInquiryForm.controls.has_lift.value === value;
  }

  selectHasLift(value: boolean) {
    this.draftInquiryForm.controls.has_lift.setValue(value);
  }

  isObjectTypeActive(value: string) {
    return this.draftInquiryForm.controls.object_type.value === value;
  }

  selectObjectType(value: string) {
    this.draftInquiryForm.controls.object_type.setValue(value);
  }

  goBack() {
    this._publicInquiryManagerService.goPrevStep()
  }

  onAddressPlaceSelected(place: google.maps.places.Place) {
    if (!place.addressComponents) return;

    const get = (type: string) =>
      place.addressComponents?.find((c: google.maps.places.AddressComponent) => c.types.includes(type))?.longText ?? '';

    const streetNumber = get('street_number');
    const route = get('route');
    const postalCode = get('postal_code');
    const locality = get('locality');

    const streetParts = [route, streetNumber].filter(el => !!el);
    const address = streetParts.join(' ') || place.formattedAddress?.split(',')[0]?.trim() || '';

    this.draftInquiryForm.patchValue({
      address,
      ...(postalCode ? { zip: postalCode } : {}),
      ...(locality ? { city: locality } : {}),
    });
  }

  async onZipPlaceSelected(place: google.maps.places.Place) {
    if (!place.addressComponents) return;

    const get = (type: string) =>
      place.addressComponents?.find((c: google.maps.places.AddressComponent) => c.types.includes(type))?.longText ?? '';

    const postalCode = get('postal_code');
    const locality = get('locality');

    this.draftInquiryForm.patchValue({ zip: postalCode, city: locality });

    const currentAddress = this.draftInquiryForm.controls.address.value;
    if (currentAddress && locality) {
      const country = this.draftInquiryForm.controls.country.value.toLowerCase();
      const found = await this._googlePlacesService.hasAddressInCity(currentAddress, locality, country);
      if (!found) this.draftInquiryForm.controls.address.setValue('');
    }
  }

  async onCityPlaceSelected(place: google.maps.places.Place) {
    if (!place.addressComponents) return;

    const get = (type: string) =>
      place.addressComponents?.find((c: google.maps.places.AddressComponent) => c.types.includes(type))?.longText ?? '';

    const locality = get('locality');
    let postalCode = get('postal_code');

    if (locality && !postalCode) {
      const currentPostalCode = this.draftInquiryForm.controls.zip.value;
      const found = await this._googlePlacesService.hasCityPostalCode(currentPostalCode, locality);
      postalCode = found ? currentPostalCode : '';
    }
    this.draftInquiryForm.patchValue({ city: locality, zip: postalCode });

    const currentAddress = this.draftInquiryForm.controls.address.value;
    if (currentAddress && locality) {
      const country = this.draftInquiryForm.controls.country.value.toLowerCase();
      const found = await this._googlePlacesService.hasAddressInCity(currentAddress, locality, country);
      if (!found) this.draftInquiryForm.controls.address.setValue('');
    }
  }

  protected _loadFormData() {
    const step2_5 = this._publicInquiryManagerService.inquiryCreationProgress?.step2_5;
    if (!step2_5) return;
    this.draftInquiryForm.patchValue({
      address: step2_5.to_address,
      zip: step2_5.to_zip,
      city: step2_5.to_city,
      country: step2_5.to_country,
      object_type: step2_5.to_object_type,
      floor: step2_5.to_floor,
      has_lift: step2_5.to_has_lift,
    });
  }

  protected _buildPayload(): CreateInquiryStep2_5 {
    const v = this.draftInquiryForm.getRawValue();
    return {
      to_address: v.address,
      to_zip: v.zip,
      to_city: v.city,
      to_country: v.country,
      to_object_type: v.object_type,
      to_floor: v.floor,
      to_has_lift: v.has_lift,
    };
  }
}
